// Vendor: Upcoming appointments list with complete/cancel actions
import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, CheckCircle2, XCircle, RefreshCw, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const STATUS_STYLES = {
  booked:              { bg: 'bg-emerald-500/20 border-emerald-400', text: 'Booked' },
  rescheduled:         { bg: 'bg-amber-500/20 border-amber-400', text: 'Rescheduled' },
  completed:           { bg: 'bg-sky-500/20 border-sky-400', text: 'Completed' },
  cancelled_by_buyer:  { bg: 'bg-rose-500/20 border-rose-400', text: 'Cancelled (buyer)' },
  cancelled_by_shop:   { bg: 'bg-rose-700/20 border-rose-500', text: 'You cancelled' },
  no_show:             { bg: 'bg-slate-500/20 border-slate-500', text: 'No-show' },
};

export default function VendorAppointmentsBlock() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('upcoming');
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/marketplace/my-appointments');
      setRows(r.data.as_vendor || []);
      setSelected(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const complete = async (a) => {
    const paid = Number(a.amount_paid_at || 0);
    const due  = Number(a.amount_remaining_at || 0);
    const collect = due > 0 ? window.confirm(`Also auto-collect ${due} AT remaining from buyer's wallet?\n(OK = collect · Cancel = skip and settle only ${paid} AT)`) : false;
    if (!window.confirm(`Mark complete? ${paid} AT will be released to you now${collect ? ` + ${due} AT will be pulled from buyer` : ''}.`)) return;
    try {
      const r = await api.post(`/marketplace/appointment/${a.id}/complete`, { collect_remaining: collect });
      toast.success(`✅ Complete · ${r.data.total_transferred_at} AT credited`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const cancel = async (id) => {
    if (!window.confirm('Cancel this appointment? Buyer will be auto-refunded from your side.')) return;
    try { await api.post(`/marketplace/appointment/${id}/cancel`); toast.success('❌ Cancelled + auto-refunded to buyer'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const noShow = async (a) => {
    const refund = Number(a.amount_paid_at || 0);
    // Wave 35 · reason code dropdown
    const reasons = [
      { code: 'no_response', label: '📵 No response / no reply' },
      { code: 'wrong_slot',  label: '⏱ Wrong slot / missed timing' },
      { code: 'weather',     label: '🌧 Weather / act of god' },
      { code: 'late_arrival',label: '🐌 Arrived too late' },
      { code: 'other',       label: '❓ Other' },
    ];
    const codeInput = window.prompt(
      `Mark @${a.buyer_name || a.buyer_email} as no-show?\n${refund} AT will be auto-refunded to buyer.\n\nReason (type number):\n${reasons.map((r,i)=>`${i+1}. ${r.label}`).join('\n')}`,
      '1'
    );
    if (codeInput === null) return;
    const idx = Math.max(1, Math.min(reasons.length, parseInt(codeInput || '1', 10))) - 1;
    const reason_code = reasons[idx].code;
    try {
      const r = await api.post(`/marketplace/appointment/${a.id}/no-show`, { reason_code });
      toast.success(`😕 No-show (${reason_code}) · ${r.data.refund_amount_at} AT refunded`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const bulkComplete = async () => {
    if (selected.size === 0) return toast.error('Select at least one');
    if (selected.size > 50) return toast.error('Max 50 per batch');
    const collect = window.confirm(`Batch-complete ${selected.size} appointments.\nAlso auto-collect any remaining balances from buyers where wallet has enough?`);
    if (!window.confirm(`Confirm: settle ${selected.size} appointments ${collect ? 'WITH' : 'without'} auto-collection.`)) return;
    try {
      const r = await api.post('/marketplace/appointments/bulk-complete', {
        booking_ids: Array.from(selected), collect_remaining: collect });
      toast.success(`✅ ${r.data.completed_count} completed · ${r.data.total_transferred_at} AT credited${r.data.failed?.length ? ` · ${r.data.failed.length} skipped` : ''}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk complete failed'); }
  };

  const filtered = rows.filter((r) => {
    if (filter === 'upcoming') return ['booked', 'rescheduled'].includes(r.status);
    if (filter === 'past') return ['completed', 'cancelled_by_buyer', 'cancelled_by_shop', 'no_show'].includes(r.status);
    return true;
  });

  return (
    <div className="rounded-2xl bg-slate-900/70 border border-emerald-400/40 p-4 space-y-2" data-testid="vendor-appointments-block">
      <div className="flex items-center justify-between">
        <div className="text-sm font-black text-emerald-200 flex items-center gap-1">
          <Calendar size={14} /> Appointments ({rows.length})
        </div>
        <div className="flex gap-1">
          {['upcoming', 'past', 'all'].map(k => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`vendor-apts-filter-${k}`}
                    className={`px-2 py-0.5 rounded text-[10px] font-black ${filter === k ? 'bg-emerald-400 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>
              {k}
            </button>
          ))}
          <button onClick={load} data-testid="vendor-apts-refresh" className="px-2 py-0.5 rounded bg-slate-700 text-[10px] font-black">
            <RefreshCw size={10} className={loading ? 'animate-spin inline' : 'inline'} />
          </button>
        </div>
      </div>

      {/* Wave 33 · Bulk-complete bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-500/15 border border-emerald-400/40" data-testid="vendor-apts-bulk-bar">
          <span className="text-[11px] font-black text-emerald-200">🎯 {selected.size} selected</span>
          <button onClick={() => setSelected(new Set())} className="text-[10px] px-2 py-0.5 rounded bg-slate-800 border border-slate-700 hover:bg-slate-700">Clear</button>
          <button onClick={bulkComplete} data-testid="vendor-apts-bulk-complete"
                  className="ml-auto px-3 py-1 rounded bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-900 text-[11px] font-black hover:brightness-110">
            ⚡ Bulk Complete ({selected.size})
          </button>
        </div>
      )}

      {filtered.length === 0 && (
        <div className="text-center py-6 opacity-60 text-xs">No {filter} appointments</div>
      )}

      <div className="space-y-1.5 max-h-96 overflow-y-auto">
        {filtered.map((a) => {
          const s = STATUS_STYLES[a.status] || { bg: 'bg-slate-800/40 border-slate-700', text: a.status };
          return (
            <div key={a.id} data-testid={`vendor-apt-${a.id.slice(0,8)}`}
                 className={`p-2 rounded-lg border ${s.bg}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  {['booked', 'rescheduled'].includes(a.status) && (
                    <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSel(a.id)}
                           data-testid={`vendor-apt-select-${a.id.slice(0,8)}`}
                           className="mt-1" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-black flex items-center gap-1"><UserIcon size={11} /> {a.buyer_name || a.buyer_email}</div>
                    <div className="text-[10px] opacity-80">
                      📅 {a.date} · {a.mode === 'virtual' ? '💻 Virtual' : '🏬 Physical'} · 💰 {a.amount_paid_at} AT paid
                      {a.amount_remaining_at > 0 && <span> · <span className="text-yellow-300">{a.amount_remaining_at} AT due</span></span>}
                    </div>
                    {a.notes && <div className="text-[10px] opacity-60 italic mt-0.5">"{a.notes}"</div>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-slate-900/50">{s.text}</span>
                  {['booked', 'rescheduled'].includes(a.status) && (
                    <div className="flex gap-1 flex-wrap justify-end">
                      <button onClick={() => complete(a)} data-testid={`vendor-apt-complete-${a.id.slice(0,8)}`}
                              title={`Release ${a.amount_paid_at} AT from escrow${a.amount_remaining_at > 0 ? ` + optionally collect ${a.amount_remaining_at} AT due` : ''}`}
                              className="px-2 py-0.5 rounded bg-emerald-500 text-slate-900 text-[10px] font-black flex items-center gap-0.5 hover:brightness-110">
                        <CheckCircle2 size={10} /> Complete + Release AT
                      </button>
                      <button onClick={() => noShow(a)} data-testid={`vendor-apt-noshow-${a.id.slice(0,8)}`}
                              title="Mark buyer no-show · auto-refund"
                              className="px-2 py-0.5 rounded bg-amber-600 text-white text-[10px] font-black flex items-center gap-0.5 hover:brightness-110">
                        😕 No-Show
                      </button>
                      <button onClick={() => cancel(a.id)} data-testid={`vendor-apt-cancel-${a.id.slice(0,8)}`}
                              className="px-2 py-0.5 rounded bg-rose-600 text-white text-[10px] font-black flex items-center gap-0.5">
                        <XCircle size={10} /> Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
